import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { v4 as uuidv4 } from "uuid";

import axios from "axios";

axios.defaults.headers.common["AgencyID"] = window.configData.AGENCYID;
axios.defaults.headers.common["Version"] = import.meta.env.VITE_APP_VERSION;
axios.defaults.headers.common["App"] = window.configData.APP;

export const verifyFare = createAsyncThunk("flight/verifyfare", async (payload, thunkAPI) => {
  try {
    if (payload.offer.source === "trios") {
      const response = await axios.post("https://www2.tstsrv.de/ratehawkapi/api/trios/verify", {
        outboundOfferId: payload.offer.fare.outboundOfferId || payload.offer.fare.offerId,
        ...(payload.offer.fare.inboundOfferId ? { inboundOfferId: payload.offer.fare.inboundOfferId } : {}),
        priceDetailBehaviorType: "STRICT_MATCH",
      });
      console.log(response.data);
      return response.data;
    }

    const response = await axios.post("https://www2.tstsrv.de/ratehawkapi/api/airtuerk/verify", {
      availableFareWebServiceLogIdReference: payload.offer.fare.webServiceLogId,
      fareid: payload.offer.fare.id,
      segments: payload.offer.OfferID.split("|"),
    });
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.log(error);
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

export const fetchFares = createAsyncThunk("flight/fetchfares", async (payload, thunkAPI) => {
  let state = thunkAPI.getState();

  if (!state.properties.agencyproperties || Object.keys(state.properties.agencyproperties).length === 0) {
    return thunkAPI.rejectWithValue({ error: "agencyproperties not loaded yet" });
  }

  if (state.search.stations.length < 2) {
    return thunkAPI.rejectWithValue({
      error: "departure and destination required",
    });
  }
  let segments;

  if (state.search.gabelflug) {
    segments = (state.search.gabelSegments ?? [])
      .filter((s) => s.departure?.value && s.destination?.value && s.date)
      .map((s) => ({ date: s.date, departure: s.departure.value, destination: s.destination.value }));
    if (segments.length < 2) {
      return thunkAPI.rejectWithValue({ error: "Gabelflug: mindestens 2 vollständige Segmente erforderlich" });
    }
  } else {
    segments = [
      {
        date: state.search.travelDate.from.replaceAll('"', "").split("T")[0],
        departure: state.search.stations[0].value,
        destination: state.search.stations[1].value,
      },
    ];
    if (!state.search.oneway) {
      segments.push({
        date: state.search.travelDate.until.replaceAll('"', "").split("T")[0],
        departure: state.search.stations[1].value,
        destination: state.search.stations[0].value,
      });
    }
  }
  let queryid = uuidv4();

  const providers = state.properties.agencyproperties?.ibeProviders;
  const hasInvolatus = Array.isArray(providers) && providers.some((p) => p.provider === "involatus");
  const useTrios = hasInvolatus && !state.search.gabelflug;

  if (hasInvolatus && !useTrios) {
    return thunkAPI.rejectWithValue({ error: "Trios unterstützt kein Gabelflug" });
  }

  try {
    if (useTrios) {
      const cabinTypes = [
        ...(state.search.firstclass ? ["FIRST_CLASS"] : []),
        ...(state.search.businessclass ? ["BUSINESS"] : []),
        ...(state.search.economyclass ? ["ECONOMY"] : []),
      ];
      const response = await axios.post("https://www2.tstsrv.de/ratehawkapi/api/trios", {
        origin: segments[0].departure,
        originType: "AIRPORT",
        destination: segments[0].destination,
        destinationType: "AIRPORT",
        departureDate: segments[0].date,
        ...(segments[1] ? { returnDate: segments[1].date } : {}),
        adultCount: state.search.adults.value * 1,
        ...(state.search.children.value * 1 > 0 ? { childCount: state.search.children.value * 1 } : {}),
        includeBrandDetails: true,
        includePersonPrice: true,
        ...(cabinTypes.length ? { cabinTypes } : {}),
      });

      return { data: { offers: response.data.offers, fares: [] }, queryid: queryid };
    }

    const response = await axios.post("https://www2.tstsrv.de/ratehawkapi/api/airtuerk", {
      adults: state.search.adults.value * 1,
      children: state.search.children.value * 1 > 0 ? state.search.children.value * 1 : undefined,
      directflight: state.search.directflight,
      nofrill: state.search.nofrill,
      fsciata: state.search.fsciata,
      fscnego: state.search.fscnego,
      fscconso: state.search.fscconso,
      fsccorp: state.search.fsccorp,
      queryid: queryid,
      promosession: state.system.sessionId,
      firstclass: state.search.firstclass,
      verifiedfares: state.search.verifiedfares,
      economyclass: state.search.economyclass,
      businessclass: state.search.businessclass,
      maxstops: state.search.maxstops,
      baggageonly: state.search.baggageonly,
      segments: segments,
    });

    return { data: response.data, queryid: queryid };
  } catch (error) {
    console.log(error);
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

const initialState = {
  fares: [],
  offers: [],
  queryid: "",
  loading: false,
};

export const flightSlice = createSlice({
  name: "flight",
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(fetchFares.pending, (state) => {
      state.loading = true;
      state.fares = [];
      state.offers = [];
    });
    builder.addCase(fetchFares.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.queryid = payload.queryid;
      state.fares = payload.data.fares;
      state.offers = payload.data.offers;
    });
    builder.addCase(fetchFares.rejected, (state) => {
      state.loading = false;
      state.fares = [];
      state.offers = [];
    });

    builder.addCase(verifyFare.pending, () => {});
    builder.addCase(verifyFare.fulfilled, (state, { payload, meta }) => {
      state.offers = state.offers.map((e) => (e.OfferID === meta.arg.offer.OfferID ? { ...e, verifiedFare: payload } : { ...e }));
    });
    builder.addCase(verifyFare.rejected, () => {});
  },
});

//export const {} = flightSlice.actions;

export default flightSlice.reducer;
